/**
 * Per-page copy for the signed-out selling layer and the "About this data"
 * block: what the visitor is looking at, what ORTEX Advanced adds on THIS
 * page, and plain definitions of the metrics on it.
 *
 * One table drives three surfaces, so they cannot drift apart:
 *   - `LockedValuePanel` (signed-out visitors only): headline, unlock bullets, offer;
 *   - `AboutThisData` (server-rendered, so crawlers and AI assistants can read and
 *     quote the definitions without logging in);
 *   - the page's Dataset / DefinedTermSet JSON-LD.
 *
 * Keys are Next.js pathname patterns (`router.pathname`), so one entry covers
 * every ticker. Pages not listed here get neither surface.
 *
 * Wording rule: describe what the data IS. ORTEX sells data, not advice; ad
 * review files "signals / ideas" wording under regulated investing.
 */

export interface StockRef {
    name: string;
    ticker: string;
    exchangesymbol: string;
    /** Stock pages have it; the free-stock match uses it (src/lib/freeStocks.ts). */
    security_id?: number | null;
}

export interface MetricDefinition {
    term: string;
    definition: string;
    /**
     * The question people search for this term ("What is short interest?"). When set, the term is
     * drawn as an H2 with the definition under it instead of in the definition list.
     */
    question?: string;
}

export interface PageLink {
    label: string;
    href: string;
}

export interface LockedPage {
    /** Stable id, sent with every tracking event. */
    id: string;
    /** Short metric name, e.g. "Short Interest". */
    metric: string;
    /** Panel headline: what the visitor is looking at. */
    headline: (stock?: StockRef) => string;
    /** One or two sentences, visible in the signed-out HTML. */
    intro: (stock?: StockRef) => string;
    /** Exactly three things Advanced adds on this page. */
    unlocks: string[];
    /** Shown under the bullets when the page needs a caveat (e.g. an add-on). */
    note?: string;
    definitions: MetricDefinition[];
    /**
     * The page places "About this data" inside its own layout (`<AboutThisData inPage />`), so the
     * shell leaves it out below <main>. For one-screen pages whose grids fill the window: below the
     * page it would only add a page scroll under them.
     */
    aboutInPage?: boolean;
    /**
     * Only the "About this data" block: no signed-out selling panel. For pages whose data is not
     * gated, or that already explain their own access, where the block is there to say what the page is.
     */
    aboutOnly?: boolean;
    /** Links listed under the definitions, with `linksTitle` as their heading. */
    links?: PageLink[];
    linksTitle?: string;
}

const stockLabel = (stock?: StockRef) => (stock ? `${stock.name} (${stock.exchangesymbol}:${stock.ticker})` : "");

const SHORT_INTEREST_DEFINITIONS: MetricDefinition[] = [
    {
        term: "Short interest",
        definition:
            "The number of shares that have been sold short and not yet bought back, usually shown as a percentage of free float (the shares available to trade). ORTEX estimates it every day from securities-lending data, instead of waiting for exchange reports that are published only twice a month and about two weeks late.",
    },
    {
        term: "Cost to borrow",
        definition: "The annualised fee a short seller pays to borrow the shares, as a percentage of the position's value. A rising cost to borrow means lendable shares are getting scarce and holding the short is getting more expensive.",
    },
    {
        term: "Utilization",
        definition: "The share of the lendable supply of a stock that is currently on loan. Close to 100% means almost no shares are left to borrow.",
    },
    {
        term: "Days to cover",
        definition: "Short interest divided by average daily trading volume: roughly how many days of normal trading it would take for every short seller to buy back their shares.",
    },
    {
        term: "Exchange-reported short interest",
        definition: "The official figure published by the exchange or regulator (in the US, FINRA twice a month). It is accurate but delayed, which is the gap the ORTEX daily and intraday estimates fill.",
    },
];

const INSIDER_DEFINITIONS: MetricDefinition[] = [
    {
        term: "Insider transaction",
        definition: "A purchase or sale of a company's shares by its directors, officers or major shareholders, taken from the official filing (in the US, SEC Form 4, due within two business days of the trade).",
    },
    {
        term: "Open-market purchase",
        definition: "An insider buying shares on the market with their own money, as opposed to receiving them through options, awards or compensation plans. It is the transaction type most read as a signal of conviction.",
    },
    {
        term: "Cluster buy",
        definition: "Several insiders of the same company buying within a short window of each other.",
    },
    {
        term: "Insider track record",
        definition: "How a company's share price moved in the months after a given insider's past trades: whether that person's buying and selling has historically been well timed.",
    },
];

/** Shared definitions picked by term, so reordering a shared list cannot swap a page's terms. Throws on a typo. */
const pick = (list: MetricDefinition[], ...terms: string[]): MetricDefinition[] =>
    terms.map((term) => {
        const found = list.find((d) => d.term === term);
        if (!found) throw new Error(`lockedPages: no definition for "${term}"`);
        return found;
    });

/**
 * The /short-interest hub's definitions: the same copy, with the questions people search as H2s and a
 * note on short float against short interest.
 */
const [SI_DEF, CTB_DEF, UTILIZATION_DEF, DTC_DEF, EXCHANGE_SI_DEF] = pick(SHORT_INTEREST_DEFINITIONS, "Short interest", "Cost to borrow", "Utilization", "Days to cover", "Exchange-reported short interest");
const HIGH_DTC_DEF: MetricDefinition = {
    ...DTC_DEF,
    question: "What is a high short interest ratio (days to cover)?",
    definition: `${DTC_DEF.definition} The higher the figure, the longer short sellers would take to close their positions; readings above about 10 days are usually considered high, and they leave the shorts more exposed if the price rises.`,
};
const SHORT_INTEREST_HUB_DEFINITIONS: MetricDefinition[] = [
    { ...SI_DEF, question: "What is short interest?" },
    HIGH_DTC_DEF,
    {
        term: "Short float",
        question: "Short float vs short interest",
        definition:
            "Short interest is a number of shares: how many are sold short and not yet bought back. Short float is that number as a percentage of the free float, the shares available to trade, so that companies of different sizes can be compared. ORTEX reports short interest as a percentage of free float.",
    },
    { ...CTB_DEF, question: "What does cost to borrow tell you?" },
    UTILIZATION_DEF,
    EXCHANGE_SI_DEF,
];

const ALT_DATA_DEFINITIONS: MetricDefinition[] = [
    {
        term: "Alternative data",
        definition: "Data about a company's business from sources other than its own filings, such as registrations, reactor output, card spend or web traffic, often published before the company reports.",
    },
    {
        term: "Leading indicator",
        definition: "A series whose changes have tended to come before changes in a figure the company reports. ORTEX shows a lead only when a backtest found it significant on data the test was not fitted to.",
    },
];

export const LOCKED_PAGES: Record<string, LockedPage> = {
    "/s/[exchange]/[ticker]/short-interest": {
        id: "stock_short_interest",
        metric: "Short Interest",
        headline: (s) => `${s?.ticker ?? "This stock"} short interest, cost to borrow and days to cover`,
        intro: (s) =>
            `This page tracks how heavily ${stockLabel(s) || "this stock"} is being shorted: ORTEX's daily short interest estimate, the cost to borrow the shares, utilization of the lendable supply and days to cover, next to the official exchange-reported figure.`,
        unlocks: [
            "Live intraday short interest estimates, updated through the trading day instead of at the previous close",
            "Every figure on this page unblurred: cost to borrow, utilization, days to cover and the ORTEX Short Score",
            "Full history, not just the last 9 months, to compare today's positioning with past squeezes",
        ],
        definitions: SHORT_INTEREST_DEFINITIONS,
    },
    "/s/[exchange]/[ticker]/insiders": {
        id: "stock_insiders",
        metric: "Insiders",
        headline: (s) => `${s?.ticker ?? "This stock"} insider buying and selling`,
        intro: (s) => `Who inside ${stockLabel(s) || "this company"} is buying or selling its shares, how big the trades are, what each insider still holds, and how the stock moved after their earlier trades, all taken from official filings.`,
        unlocks: [
            "The full transaction list and current insider positions, unblurred",
            "Each insider's track record: how the share price moved after their past trades",
            "Insider filings from 54 markets, so non-US holdings and directors are covered too",
        ],
        definitions: INSIDER_DEFINITIONS,
    },
    "/s/[exchange]/[ticker]/dividend": {
        id: "stock_dividend",
        metric: "Dividend",
        headline: (s) => `${s?.ticker ?? "This stock"} dividends: declared amounts, dates, history and income`,
        intro: (s) =>
            `When ${stockLabel(s) || "this company"} declares its dividends and goes ex, every past payment adjusted for splits, its stated dividend guidance, how well earnings and cash flow cover the payout, and borrowing around record dates, with ORTEX forecasts of the next dividends: dates, amount ranges and the chance of a raise or cut.`,
        unlocks: [
            "Every declared amount with the split-adjusted history, cover, payout ratio and dividend guidance",
            "Borrowing around the record date and the expected price drop on the ex-date",
            "ORTEX forecasts of the next dividends with amount ranges, the chance of a raise or cut, and alerts when a dividend is declared or goes ex",
        ],
        definitions: [
            {
                term: "Ex-dividend date",
                definition: "The first trading day on which a buyer of the shares no longer receives the next dividend. Holders at the close of the day before are paid.",
            },
            {
                term: "Record and pay date",
                definition: "The record date is when the company checks who holds the shares; the pay date is when the dividend is paid to them.",
            },
            {
                term: "Forward yield",
                definition: "The dividends ORTEX expects over the next twelve months divided by the last share price.",
            },
            {
                term: "Payout ratio",
                definition: "The share of earnings, or of free cash flow, that is paid out as dividends. Above 100% the dividend is paid from more than the company earns.",
            },
        ],
    },
    "/short-interest": {
        id: "short_interest_hub",
        metric: "Short Interest",
        headline: () => "Short interest across global markets",
        intro: () => "The most shorted, most expensive to borrow and fastest-changing short positions across US, European and Asian stocks, from ORTEX's daily short interest estimates rather than twice-monthly exchange reports.",
        unlocks: [
            "Live short screener: filter every stock on short interest, cost to borrow, utilization and days to cover",
            "Intraday changes in short interest, not just the previous close",
            "Full history and every row unblurred across all markets",
        ],
        definitions: SHORT_INTEREST_HUB_DEFINITIONS,
        linksTitle: "More on short interest",
        links: [
            { label: "Short squeeze candidates", href: "/short-interest/short-squeeze" },
            { label: "Short interest by index", href: "/short-index" },
            { label: "ORTEX glossary of short-selling terms", href: "https://public.ortex.com/glossary" },
        ],
    },
    "/short-interest/short-squeeze": {
        id: "short_squeeze",
        metric: "Short Squeeze Candidates",
        headline: () => "Short squeeze candidates",
        intro: () =>
            "Stocks with a market value of at least $500 million, ranked by the ORTEX Short Score, which combines short interest as a percentage of free float, cost to borrow, utilization of the lendable supply and days to cover. A high score means the short side is crowded and expensive to hold, not that a squeeze will happen.",
        unlocks: ["The full ranked list, every row unblurred, across all markets", "Live intraday short interest and cost to borrow on every candidate", "Filters on short interest, cost to borrow, utilization and days to cover"],
        definitions: [
            {
                term: "Short squeeze",
                question: "What is a short squeeze?",
                definition: "A sharp rise in a heavily shorted stock as short sellers buy back shares to limit losses, which pushes the price higher still.",
            },
            HIGH_DTC_DEF,
            CTB_DEF,
            UTILIZATION_DEF,
        ],
        linksTitle: "More on short interest",
        links: [
            { label: "Most shorted stocks today", href: "/short-interest" },
            { label: "ORTEX glossary of short-selling terms", href: "https://public.ortex.com/glossary" },
        ],
    },
    "/options": {
        id: "options_hub",
        metric: "Options",
        headline: () => "Options flow and unusual options activity",
        intro: () => "US options trades as they print, the trades that stand out against a contract's normal volume, and where dealers' hedging is likely to add to or damp price moves.",
        unlocks: [],
        aboutOnly: true,
        definitions: [
            {
                term: "Options flow",
                question: "What is options flow?",
                definition: "The stream of options trades as they print on the exchanges, with the premium paid, strike, expiry, size and whether each trade was bought at the ask or sold at the bid.",
            },
            {
                term: "Unusual options activity",
                question: "What is unusual options activity?",
                definition: "Trades or volume in a contract well above its normal level, or above its open interest: new positions large enough to stand out from the day's flow.",
            },
            {
                term: "Gamma exposure",
                question: "What is gamma exposure?",
                definition: "An estimate of how much stock options dealers would have to buy or sell to stay hedged as the price moves. Where it is positive their hedging tends to damp moves; where it is negative it tends to add to them.",
            },
        ],
    },
    "/heatmap": {
        id: "heatmap",
        metric: "Stock Market Heatmap",
        headline: () => "Stock market heatmaps",
        intro: () =>
            "Heatmaps of the S&P 500 and other indexes grouped by sector: each stock sized by market value and colored by price change, short interest, options activity or another ORTEX measure, so the sectors and stocks that stand out are visible at once.",
        unlocks: [],
        aboutOnly: true,
        definitions: [
            {
                term: "Stock heatmap",
                definition: "A treemap of an index's stocks, grouped by sector, where each tile's size and color show two measures at once, for example market value and the week's price change.",
            },
        ],
    },
    "/screening": {
        id: "screening",
        metric: "Stock Screener",
        headline: () => "A stock screener with short interest, options and ORTEX scores",
        intro: () => "Filter stocks across global markets on short interest, cost to borrow, options activity, fundamentals, analyst estimates and ORTEX scores, then sort and export the result.",
        unlocks: [],
        aboutOnly: true,
        definitions: [
            {
                term: "Stock screener",
                definition: "A tool that filters a universe of stocks on criteria such as market value, valuation or short interest, and lists the stocks that pass.",
            },
        ],
        linksTitle: "Example screens",
        links: [
            { label: "Short squeeze candidates", href: "/short-interest/short-squeeze" },
            { label: "Live short interest screener", href: "/short-interest?tab=live_short_screener" },
            { label: "Dividend screener", href: "/dividends?tab=screener" },
            { label: "Upcoming and reverse stock splits", href: "/stock-splits" },
            { label: "Highest ORTEX stock scores", href: "/stock-scores" },
        ],
    },
    "/insiders": {
        id: "insiders_hub",
        metric: "Insiders",
        headline: () => "Where corporate insiders are buying and selling",
        intro: () => "Insider trades from official filings in 54 markets, ranked by company, sector, market and person, including cluster buys and the insiders whose past trades were best timed.",
        unlocks: ["Every trade and every insider across 54 markets, with full history", "Insider track records: which directors' past trades were followed by the biggest moves", "Cluster buys and sector views as they are filed"],
        definitions: INSIDER_DEFINITIONS,
    },
    "/buybacks": {
        id: "buybacks",
        metric: "Buybacks",
        headline: () => "What companies actually spent on buybacks",
        intro: () => "Quarterly share repurchases as reported in company filings, the authorization each company has left, and the buyback as a percentage of float, so announced programmes can be told apart from money actually spent.",
        unlocks: [
            "Buybacks next to live short interest and cost to borrow, to find shrinking float against a crowded short",
            "ORTEX's full stock pages behind every company on the list: short interest, insiders, options and fundamentals",
            "Full history across every covered market",
        ],
        definitions: [
            {
                term: "Stock buyback",
                question: "What are stock buybacks?",
                definition:
                    "A stock buyback is a company repurchasing its own shares. What a company actually spent in a period is reported in its filings (in the US, the 10-Q or 10-K); announced programmes are statements of intent and are often not completed.",
            },
            {
                term: "Executed buyback",
                definition: "The amount a company actually spent repurchasing its own shares in a period, as reported in its filings (in the US, the 10-Q or 10-K). Announced programmes are statements of intent and are often not completed.",
            },
            {
                term: "Remaining authorization",
                definition: "How much of the repurchase programme approved by the board is still unspent.",
            },
            {
                term: "Buyback as % of float",
                definition: "Shares repurchased as a percentage of the shares available to trade: how much the company is shrinking the tradeable supply.",
            },
        ],
    },
    "/us-gov-trades": {
        id: "us_gov_trades",
        metric: "US Government Trades",
        headline: () => "What members of Congress are buying and selling",
        intro: () => "Stock trades disclosed by US members of Congress and other officials, with the trade date, the disclosure date and the reported value range.",
        unlocks: [
            "The full disclosure history for every official, unblurred",
            "ORTEX's full stock pages behind every traded name: short interest, insiders and options",
            "Live short interest and cost to borrow next to the stocks officials are trading",
        ],
        definitions: [
            {
                term: "STOCK Act disclosure",
                definition: "US law requires members of Congress to report trades over $1,000 within 45 days. Values are disclosed as ranges, not exact amounts.",
            },
            {
                term: "Disclosure lag",
                definition: "The days between the trade itself and the date it was publicly reported.",
            },
        ],
    },
    "/ipo-deals": {
        id: "ipo_deals",
        metric: "IPOs and Equity Deals",
        headline: () => "IPO calendar and equity deal pipeline",
        intro: () => "Upcoming and recent IPOs and follow-on equity offerings with pricing, valuation and lock-up details.",
        unlocks: ["Every deal in the pipeline unblurred, with pricing and valuation", "Lock-up expiry dates and sizes for recent listings", "Short interest and cost to borrow on newly listed stocks"],
        definitions: [
            {
                term: "IPO pricing",
                definition: "The price at which shares are sold to investors in an initial public offering, set the evening before trading starts, often against an earlier indicated range.",
            },
            {
                term: "Follow-on offering",
                definition: "A sale of new or existing shares by a company that is already listed. New shares dilute existing holders.",
            },
            {
                term: "Lock-up",
                definition: "A period after an IPO, typically 90 to 180 days, in which insiders and pre-IPO investors agree not to sell.",
            },
        ],
    },
    "/lock-ups": {
        id: "lock_ups",
        metric: "Lock-up Expiries",
        headline: () => "Lock-up expiries and the supply they release",
        intro: () => "Which restricted shares become sellable and when, sized against free float, average daily volume and market value, so it is clear whether an expiry is big enough to matter.",
        unlocks: ["Every expiry unblurred, with shares released as % of float and days of volume", "Short interest and cost to borrow on stocks approaching expiry", "What past expiries did to the share price"],
        definitions: [
            {
                term: "Lock-up expiry",
                definition: "The date on which insiders and pre-IPO investors are free to sell shares they agreed to hold after a listing, usually 90 to 180 days after the IPO.",
            },
            {
                term: "Shares released as % of float",
                definition: "The newly sellable shares compared with the shares already trading: the larger it is, the more potential supply hits the market.",
            },
        ],
    },
    "/earnings": {
        id: "earnings",
        metric: "Earnings",
        headline: () => "Who reports in the next two weeks",
        intro: () => "Upcoming earnings with each company's historical earnings reactions, the options-implied move, short interest and alternative-data leads.",
        unlocks: ["Alternative-data leads on reporting companies: card spend, web traffic and other datasets that move ahead of results", "Live short interest and cost to borrow going into each report", "Full history of past earnings reactions"],
        definitions: [
            {
                term: "Options-implied move",
                definition: "The share price move, up or down, that options prices imply for the day after earnings, derived from the price of the at-the-money straddle.",
            },
            {
                term: "Earnings reaction",
                definition: "How far the share price moved on the trading day after past earnings reports.",
            },
        ],
    },
    "/earnings/heatmap": {
        id: "earnings_heatmap",
        metric: "Earnings Heatmap",
        headline: () => "Upcoming earnings in one heatmap",
        intro: () => "Upcoming earnings, beats, misses and post-earnings price moves in a single heatmap view.",
        unlocks: ["Live short interest and cost to borrow going into each report", "Alternative-data leads on reporting companies", "Full history of past earnings reactions"],
        definitions: [
            {
                term: "Earnings beat / miss",
                definition: "Whether reported earnings per share came in above or below the analyst consensus estimate.",
            },
            {
                term: "Earnings reaction",
                definition: "How far the share price moved on the trading day after past earnings reports.",
            },
        ],
    },
    "/index-rebalance": {
        id: "index_rebalance",
        metric: "Index Rebalance",
        headline: () => "Index rebalance announcements and forecasts",
        intro: () => "Additions, deletions and weight changes in major equity indexes, with announcement and effective dates and the passive buying or selling each change implies.",
        unlocks: ["ORTEX Advanced is the plan the Index Rebalance Forecasts add-on is built on", "Short interest and cost to borrow on every stock moving in or out of an index", "ORTEX's full stock pages behind every name on the list"],
        note: "Forecasts of upcoming index changes are a separate add-on for Advanced subscribers.",
        aboutInPage: true,
        definitions: [
            {
                term: "Index rebalance",
                definition: "A scheduled review in which an index provider adds, removes or reweights constituents. Funds tracking the index then have to buy or sell to match it.",
            },
            {
                term: "Announcement and effective date",
                definition: "The day the provider publishes the change, and the day it takes effect. Index funds trade at the close before the effective date.",
            },
            {
                term: "Passive demand",
                definition: "The estimated value of shares index-tracking funds must buy (or sell), often expressed in days of average trading volume.",
            },
        ],
    },
    "/dividends": {
        id: "dividends",
        metric: "Dividends",
        headline: () => "Dividend calendar and income: what every company has declared, and when it pays",
        intro: () =>
            "Declared dividends, ex-dates and pay dates across global markets, with each company's dividend history adjusted for splits and its stated dividend guidance. Dates are open to everyone, and ORTEX forecasts the next dividends in every market, with its track record alongside.",
        unlocks: [
            "Every declared amount next to the company's history, cover and dividend guidance, and the expected price drop on each ex-date",
            "Your portfolio's dividend income month by month, net of withholding tax for where you live, with reinvestment",
            "ORTEX forecasts of every upcoming dividend, alerts when a dividend is declared or goes ex, and a weekly dividend digest",
        ],
        aboutInPage: true,
        definitions: [
            {
                term: "Ex-dividend date",
                question: "What is an ex-dividend date?",
                definition: "The first trading day on which a buyer of the shares no longer receives the next dividend. To be paid, you must own the shares before the ex-date; the share price usually drops by about the dividend on that morning.",
            },
            {
                term: "Record date",
                definition:
                    "The day the company checks its register to see who is paid. With one-day settlement (US and Canada since May 2024) it is usually the same day as the ex-date; in markets that settle in two days it is the business day after.",
            },
            {
                term: "Forecast range",
                definition: "The amount ORTEX expects the company to declare, with a low and a high figure around it. How often declared amounts fell inside the range is shown on the track record.",
            },
            {
                term: "Chance of a change",
                definition: "How likely the next dividend is to differ from the last one: the chance of a raise plus the chance of a cut or an omission.",
            },
        ],
    },
    "/stock-movers": {
        id: "stock_movers",
        metric: "Stock Movers",
        headline: () => "Today's biggest gainers, losers and most active stocks",
        intro: () => "The stocks moving most today across global markets and sectors, with volume against normal levels.",
        unlocks: ["Short interest and cost to borrow next to every mover, to see when a move is a squeeze", "ORTEX's full stock pages behind every name: insiders, options and fundamentals", "Movers across every covered market, not just the US"],
        definitions: [
            {
                term: "Relative volume",
                definition: "Today's trading volume compared with the stock's average daily volume. Moves on high relative volume carry more weight.",
            },
            {
                term: "Short squeeze",
                definition: "A sharp rise in a heavily shorted stock as short sellers buy back shares to limit losses, which pushes the price higher still.",
            },
        ],
    },
    // The Alt Data section (Advanced and above). Nothing on these pages is free: a viewer without the
    // entitlement gets the page's own text and a locked preview (AltDataLockedView), never the data.
    "/alt-data": {
        id: "alt_data",
        metric: "Alt Data",
        headline: () => "Alternative data mapped to listed companies",
        intro: () =>
            "Datasets from outside company filings, such as nuclear reactor output, monthly company revenue, consumer credit, bank call reports and clinical trial timelines, each mapped to the stocks it reaches. A series is shown as a leading indicator only once a backtest found its lead on a reported figure out of sample.",
        unlocks: ["Every Alt Data dataset, charted over its full history", "Each series mapped to the tickers it reaches, with its latest changes", "Leading indicators by sector, with how many days each series has led a reported figure"],
        note: "Alt Data is included with ORTEX Advanced, Professional and Institutional.",
        definitions: ALT_DATA_DEFINITIONS,
    },
    "/alt-data/carmakers": {
        id: "alt_data_carmakers",
        metric: "Car registrations by carmaker",
        headline: () => "EV and new-car registrations by carmaker, country by country",
        intro: () =>
            "Monthly new-car registrations for Tesla, BMW, Mercedes-Benz, Volkswagen, Stellantis, Renault and BYD in each European country, read from the national registers, with a Europe total and a list of the countries the total leaves out and why.",
        unlocks: ["Each carmaker's registrations by country, with the change on a year earlier", "The company's share of each national market and the marques within a group", "The full monthly history behind the chart"],
        note: "Alt Data is included with ORTEX Advanced, Professional and Institutional.",
        definitions: [
            {
                term: "New-car registration",
                definition: "A new vehicle entered in a country's official vehicle register for the first time. Registrations are published monthly and are the closest public figure to deliveries in that country.",
            },
            {
                term: "Market share",
                definition: "The carmaker's registrations as a percentage of all new-car registrations in that country for the month.",
            },
            ...pick(ALT_DATA_DEFINITIONS, "Alternative data"),
        ],
    },
    "/alt-data/attention-positioning": {
        id: "alt_data_attention_positioning",
        metric: "Attention x Positioning",
        headline: () => "Retail attention against short positioning",
        intro: () => "How much people are looking at a US company against its own normal level, from Wikipedia page views and ORTEX's own readership, drawn over utilization, cost to borrow and days to cover. It measures attention, not sales.",
        unlocks: ["The attention composite for each stock, over utilization, cost to borrow and days to cover", "A screener for names where attention and utilization both clear a threshold", "The history behind every chart"],
        note: "Alt Data is included with ORTEX Advanced, Professional and Institutional.",
        definitions: [
            {
                term: "Attention composite",
                definition: "Wikipedia page views and ORTEX readership for a company, each measured as a z-score against the company's own normal level and combined. High means unusually many people are looking, not that sales are rising.",
            },
            ...pick(SHORT_INTEREST_DEFINITIONS, "Utilization", "Cost to borrow", "Days to cover"),
        ],
    },
    "/macro-data": {
        id: "macro_data",
        metric: "Macro Data",
        headline: () => "Unemployment, inflation and jobless claims across countries",
        intro: () => "Unemployment, inflation, weekly jobless claims and Treasury cash, as each national statistics office publishes them, on one scale so countries can be compared.",
        unlocks: ["Every macro series charted over its full history", "Countries side by side on the same chart", "The figures as each statistics office published them"],
        note: "Macro Data is part of Alt Data, included with ORTEX Advanced, Professional and Institutional.",
        definitions: [
            {
                term: "Unemployment rate",
                definition: "People without work and looking for it, as a percentage of the labour force, as the national statistics office defines it.",
            },
            {
                term: "Inflation",
                definition: "The change in consumer prices on a year earlier, from each country's consumer price index.",
            },
            {
                term: "Weekly jobless claims",
                definition: "New claims for unemployment insurance filed in a week (in the US, published by the Department of Labor every Thursday).",
            },
        ],
    },
};

export const getLockedPage = (pathname: string): LockedPage | undefined => LOCKED_PAGES[pathname];

/** Canonical absolute URL for the page, matching the page's own `<link rel="canonical">`. */
export const lockedPageUrl = (pathname: string, stock?: StockRef): string => {
    const origin = "https://app.ortex.com";
    if (stock && pathname.startsWith("/s/[exchange]/[ticker]")) {
        return `${origin}${pathname.replace("[exchange]", stock.exchangesymbol).replace("[ticker]", stock.ticker)}`;
    }
    return `${origin}${pathname}`;
};
